import { useEffect, useRef, useState } from "react";
import Layout from "@/components/Layout";
import { api } from "@/lib/api";
import { Code2, CheckCircle2, XCircle, Play, Trophy, Lightbulb } from "lucide-react";
import { toast } from "sonner";

const LANGUAGES = [
  { id: "javascript", label: "JavaScript", color: "bg-amber-50 text-amber-700" },
  { id: "html", label: "HTML", color: "bg-orange-50 text-orange-700" },
  { id: "css", label: "CSS", color: "bg-blue-50 text-blue-700" },
  { id: "python", label: "Python", color: "bg-emerald-50 text-emerald-700" },
  { id: "c", label: "C", color: "bg-slate-100 text-slate-700" },
  { id: "cpp", label: "C++", color: "bg-violet-50 text-violet-700" },
];

const LOCAL_TASKS = {
  html: [
    { id: "html-heading", title: "First Heading", difficulty: "Easy",
      prompt: "Create an <h1> that says 'Hello TBX'. Live preview will render your HTML.",
      starter: "<h1>Hello TBX</h1>\n<p>Welcome to Smart TBX Learning Hub!</p>",
      hint: "Wrap text in <h1></h1> tags." },
    { id: "html-list", title: "Bullet List", difficulty: "Easy",
      prompt: "Create an unordered list of 3 favorite subjects.",
      starter: "<ul>\n  <li>Math</li>\n  <li>Science</li>\n  <li>Computer</li>\n</ul>",
      hint: "Use <ul> and <li> tags." },
  ],
  css: [
    { id: "css-color", title: "Style It Blue", difficulty: "Easy",
      prompt: "Style all <h1> to be blue and centered. Live preview updates as you type.",
      starter: "<style>\nh1 { color: blue; text-align: center; }\n</style>\n<h1>Learn. Code. Ship.</h1>",
      hint: "Use `color` and `text-align` properties." },
    { id: "css-card", title: "Card Component", difficulty: "Medium",
      prompt: "Create a rounded card with padding and shadow.",
      starter: "<style>\n.card { padding: 20px; border-radius: 12px; box-shadow: 0 4px 10px rgba(0,0,0,0.1); background: white; }\n</style>\n<div class=\"card\">TBX Card</div>",
      hint: "border-radius and box-shadow." },
  ],
  python: [
    { id: "py-hello", title: "Hello Python", difficulty: "Easy",
      prompt: "Print 'Hello TBX'. Runs live in your browser using Pyodide.",
      starter: "print('Hello TBX')",
      hint: "Use print() function." },
    { id: "py-sum", title: "Sum of List", difficulty: "Easy",
      prompt: "Compute and print sum of [1,2,3,4,5].",
      starter: "nums = [1, 2, 3, 4, 5]\nprint(sum(nums))",
      hint: "Python has built-in sum()." },
    { id: "py-fizz", title: "FizzBuzz to 15", difficulty: "Medium",
      prompt: "Print FizzBuzz from 1 to 15.",
      starter: "for i in range(1, 16):\n    if i % 15 == 0:\n        print('FizzBuzz')\n    elif i % 3 == 0:\n        print('Fizz')\n    elif i % 5 == 0:\n        print('Buzz')\n    else:\n        print(i)",
      hint: "Use modulo (%) to check divisibility." },
  ],
  c: [
    { id: "c-hello", title: "Hello C", difficulty: "Easy",
      prompt: "Write a C program that prints 'Hello TBX'. Execution is preview-only in this beta — your submission is tracked.",
      starter: "#include <stdio.h>\n\nint main() {\n    printf(\"Hello TBX\\n\");\n    return 0;\n}",
      hint: "Use printf() from stdio.h." },
  ],
  cpp: [
    { id: "cpp-hello", title: "Hello C++", difficulty: "Easy",
      prompt: "Write a C++ program that prints 'Hello TBX'. Execution is preview-only in this beta.",
      starter: "#include <iostream>\nusing namespace std;\n\nint main() {\n    cout << \"Hello TBX\" << endl;\n    return 0;\n}",
      hint: "Use std::cout." },
  ],
};

export default function StudentCoding() {
  const [lang, setLang] = useState("javascript");
  const [tasks, setTasks] = useState([]);
  const [me, setMe] = useState([]);
  const [active, setActive] = useState(null);
  const [code, setCode] = useState("");
  const [results, setResults] = useState(null);
  const [running, setRunning] = useState(false);
  const [output, setOutput] = useState("");
  const [showHint, setShowHint] = useState(false);
  const pyodideRef = useRef(null);
  const [pyLoading, setPyLoading] = useState(false);

  const load = async () => {
    const [t, m] = await Promise.all([api.get("/coding/tasks"), api.get("/coding/me")]);
    setTasks(t.data); setMe(m.data);
  };
  useEffect(() => { load(); }, []);

  useEffect(() => {
    // Load Pyodide on demand
    if (lang === "python" && !pyodideRef.current && !pyLoading) {
      setPyLoading(true);
      const s = document.createElement("script");
      s.src = "https://cdn.jsdelivr.net/pyodide/v0.24.1/full/pyodide.js";
      s.onload = async () => {
        try {
          // eslint-disable-next-line no-undef
          const py = await loadPyodide();
          pyodideRef.current = py;
        } catch (e) { /* ignore */ }
        finally { setPyLoading(false); }
      };
      document.body.appendChild(s);
    }
  }, [lang, pyLoading]);

  const langTasks = () => {
    if (lang === "javascript") return tasks; // backend
    return LOCAL_TASKS[lang] || [];
  };

  const start = (task) => {
    setActive(task); setCode(task.starter); setResults(null); setOutput(""); setShowHint(false);
  };

  const runJs = async () => {
    if (!active?.tests) return;
    setRunning(true);
    const rs = [];
    try {
      const factory = new Function(`${code}; return solve;`);
      const solve = factory();
      for (const t of active.tests) {
        try {
          const got = solve(...t.args);
          rs.push({ args: t.args, expected: t.expected, got, pass: JSON.stringify(got) === JSON.stringify(t.expected) });
        } catch (err) { rs.push({ args: t.args, expected: t.expected, got: String(err), pass: false }); }
      }
    } catch (err) {
      toast.error("Syntax error");
      setResults([{ args: [], expected: "", got: String(err), pass: false }]);
      setRunning(false); return;
    }
    setResults(rs);
    const passed = rs.filter((r) => r.pass).length;
    try { await api.post("/coding/submit", { task_id: active.id, passed, total: rs.length, code }); } catch { /* ignore */ }
    if (passed === rs.length) toast.success("All tests pass!"); else toast.info(`${passed}/${rs.length} passed`);
    setRunning(false); load();
  };

  const runPython = async () => {
    if (!pyodideRef.current) { toast.info("Python runtime is loading…"); return; }
    setRunning(true);
    setOutput("");
    try {
      let out = "";
      pyodideRef.current.setStdout({ batched: (s) => { out += s + "\n"; } });
      pyodideRef.current.setStderr({ batched: (s) => { out += "[err] " + s + "\n"; } });
      await pyodideRef.current.runPythonAsync(code);
      setOutput(out || "(no output)");
      await api.post("/coding/submit", { task_id: active.id, passed: 1, total: 1, code });
      toast.success("Executed");
      load();
    } catch (err) {
      setOutput(String(err));
      toast.error("Python error");
    } finally { setRunning(false); }
  };

  const runCppNotice = async () => {
    setOutput(`# ${lang.toUpperCase()} execution is preview-only in this beta.\n# Your code is saved for review by staff.\n# Focus: syntax + logic — you'll get feedback from a mentor.`);
    try { await api.post("/coding/submit", { task_id: active.id, passed: 0, total: 1, code }); } catch { /* ignore */ }
    toast.info("Submission saved for review");
    load();
  };

  const run = () => {
    if (lang === "javascript") return runJs();
    if (lang === "python") return runPython();
    if (lang === "html" || lang === "css") { /* preview updates live via iframe below */ return; }
    return runCppNotice();
  };

  const bestPass = (id) => {
    const attempts = me.filter((x) => x.task_id === id);
    if (attempts.length === 0) return null;
    const best = Math.max(...attempts.map((a) => a.passed));
    return { best, total: attempts[0].total };
  };

  if (active) {
    return (
      <Layout title={active.title} subtitle={`Coding · ${lang.toUpperCase()}`}>
        <button onClick={() => { setActive(null); setResults(null); setOutput(""); }} data-testid="back-to-tasks" className="text-sm text-slate-500 hover:text-slate-800 mb-4">← All tasks</button>
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-5">
          <div className="lg:col-span-2 bg-white border border-slate-200 rounded-2xl p-5">
            <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-1">{active.difficulty}</div>
            <h2 className="font-display font-bold text-xl text-slate-900">{active.title}</h2>
            <p className="text-sm text-slate-600 mt-2 leading-relaxed">{active.prompt}</p>
            {active.hint && (
              <button onClick={() => setShowHint((s) => !s)} data-testid="hint-btn" className="mt-3 inline-flex items-center gap-1.5 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-3 py-1.5 hover:bg-amber-100">
                <Lightbulb className="w-3.5 h-3.5" /> {showHint ? "Hide hint" : "Show hint"}
              </button>
            )}
            {showHint && active.hint && (
              <div className="mt-2 text-xs text-amber-800 bg-amber-50 border-l-4 border-amber-400 p-2 rounded" data-testid="hint-text">{active.hint}</div>
            )}
            {active.tests && (
              <div className="mt-4 text-xs text-slate-500 space-y-1">
                <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-1">Test cases</div>
                {active.tests.map((t, i) => (
                  <div key={i} className="font-mono bg-slate-50 rounded-lg p-2">
                    solve({t.args.map(JSON.stringify).join(", ")}) → {JSON.stringify(t.expected)}
                  </div>
                ))}
              </div>
            )}
          </div>
          <div className="lg:col-span-3 bg-slate-950 rounded-2xl p-4">
            <div className="flex items-center justify-between mb-2 text-xs text-slate-400">
              <span className="font-mono">{lang}</span>
              {lang !== "html" && lang !== "css" && (
                <button data-testid="run-code-btn" disabled={running || pyLoading} onClick={run} className="px-3 py-1.5 rounded-full bg-emerald-500 text-white text-xs hover:bg-emerald-600 inline-flex items-center gap-1 disabled:opacity-50">
                  <Play className="w-3 h-3" /> {running ? "Running…" : pyLoading && lang === "python" ? "Loading Python…" : "Run"}
                </button>
              )}
            </div>
            <textarea data-testid="code-editor" value={code} onChange={(e) => setCode(e.target.value)} spellCheck={false}
              className="w-full h-72 bg-slate-950 text-emerald-300 font-mono text-sm p-3 rounded-xl border border-slate-800 outline-none focus:border-emerald-500" />
            {(lang === "html" || lang === "css") && (
              <div className="mt-3 bg-white rounded-xl overflow-hidden border border-slate-800" data-testid="live-preview">
                <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 px-3 py-1.5 border-b border-slate-100">Live preview</div>
                <iframe title="preview" srcDoc={code} sandbox="" className="w-full h-64" />
              </div>
            )}
            {output && (
              <pre data-testid="code-output" className="mt-3 p-3 rounded-xl bg-black text-emerald-300 font-mono text-xs overflow-auto max-h-48 whitespace-pre-wrap">{output}</pre>
            )}
            {results && (
              <div className="mt-3 space-y-1.5" data-testid="test-results">
                {results.map((r, i) => (
                  <div key={i} className={`flex items-center gap-2 p-2 rounded-lg text-xs font-mono ${r.pass ? "bg-emerald-500/10 text-emerald-300" : "bg-red-500/10 text-red-300"}`}>
                    {r.pass ? <CheckCircle2 className="w-4 h-4" /> : <XCircle className="w-4 h-4" />}
                    <span>Input: {r.args.map(JSON.stringify).join(", ")} → got {JSON.stringify(r.got)} (expected {JSON.stringify(r.expected)})</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout title="Coding Practice" subtitle="Sharpen your syntax">
      <div className="flex gap-2 mb-6 overflow-x-auto pb-2" data-testid="lang-tabs">
        {LANGUAGES.map((L) => (
          <button key={L.id} data-testid={`lang-${L.id}`} onClick={() => setLang(L.id)}
            className={`px-4 py-2 rounded-full text-sm whitespace-nowrap ${lang === L.id ? "bg-slate-900 text-white" : `${L.color} hover:opacity-80`}`}>
            {L.label}
          </button>
        ))}
      </div>
      {(lang === "c" || lang === "cpp") && (
        <div className="mb-4 bg-amber-50 border border-amber-200 rounded-xl px-4 py-2 text-xs text-amber-800">
          <b>Beta:</b> {lang.toUpperCase()} execution isn&apos;t sandboxed yet — code is saved for staff review. Focus on syntax &amp; logic.
        </div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
        {langTasks().map((t) => {
          const b = bestPass(t.id);
          const solved = b && b.best === b.total;
          return (
            <div key={t.id} data-testid={`task-card-${t.id}`} className="bg-white border border-slate-200 rounded-2xl p-5 card-hover">
              <div className="flex items-start justify-between">
                <div className="w-10 h-10 rounded-xl bg-blue-50 text-blue-700 flex items-center justify-center"><Code2 className="w-5 h-5" /></div>
                {solved && <span className="text-[10px] uppercase tracking-[0.2em] text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-full inline-flex items-center gap-1"><Trophy className="w-3 h-3" /> Solved</span>}
              </div>
              <h3 className="mt-3 font-display font-bold text-lg text-slate-900">{t.title}</h3>
              <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 mt-0.5">{t.difficulty}</div>
              <p className="text-sm text-slate-500 mt-2 line-clamp-2">{t.prompt}</p>
              {b && <div className="mt-3 text-xs text-slate-600 font-mono">Best: {b.best}/{b.total}</div>}
              <button data-testid={`start-task-${t.id}`} onClick={() => start(t)} className="mt-4 w-full py-2 rounded-full bg-blue-600 text-white text-sm hover:bg-blue-700">
                {solved ? "Retry" : "Start"}
              </button>
            </div>
          );
        })}
        {langTasks().length === 0 && <div className="col-span-3 text-slate-500 text-sm py-6">No tasks in this language yet.</div>}
      </div>
    </Layout>
  );
}
